import { brandStoryPage } from "@/mocks/brandStory";
import GlobalBottomCta from "@/components/feature/GlobalBottomCta";
import PageMeta from "@/components/feature/PageMeta";
import ScrollReveal from "@/components/feature/ScrollReveal";

const pageMeta = {
  title: "品牌故事｜來加綉綉紅豆餅",
  description: "了解來加綉綉紅豆餅的品牌故事、創業初衷與手作精神。我們相信紅豆餅不只是街頭小吃，而是一種能傳遞溫度的日常儀式。",
  keywords: "來加綉綉紅豆餅,紅豆餅品牌故事,車輪餅品牌,手作紅豆餅,職人現烤,台灣紅豆餅創業",
  ogType: "article",
};

export default function BrandStory() {
  const { hero, origin, philosophy, spirit } = brandStoryPage;
  return (
    <main className="pt-16 md:pt-20">
      <PageMeta {...pageMeta} />
      {/* Hero */}
      <section className="relative min-h-[420px] md:min-h-[520px] flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-redbean-primary/40">
          <img
            src="https://readdy.ai/api/search-image?query=warm%20close-up%20of%20artisan%20hands%20making%20taiyaki%20red%20bean%20cake%20dough%20on%20wooden%20board%20flour%20dusting%20warm%20golden%20workshop%20lighting%20cozy%20handmade%20atmosphere%20shallow%20depth%20of%20field%20documentary%20food%20photography%20warm%20amber%20tones%20cream%20background&width=1600&height=900&seq=bs-hero-1&orientation=landscape"
            alt="手作紅豆餅職人製作過程"
            className="w-full h-full object-cover object-center"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-redbean-coffee/50 via-redbean-coffee/40 to-redbean-coffee/60" />
        </div>
        <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-8 text-center" style={{ maxWidth: "900px" }}>
          <span className="inline-block px-4 py-1.5 bg-redbean-gold text-redbean-coffee text-sm font-semibold rounded-full mb-6">
            {hero.tag}
          </span>
          <h1
            className="font-heading font-black text-redbean-cream mb-5 max-w-3xl mx-auto text-balance"
            style={{ fontSize: "clamp(2rem, 5vw, 2.75rem)", lineHeight: 1.3 }}
          >
            {hero.title}
          </h1>
          <p className="text-redbean-cream/85 max-w-xl mx-auto" style={{ fontSize: "1.1875rem", lineHeight: 1.75 }}>
            {hero.subtitle}
          </p>
        </div>
      </section>

      {/* Origin Section */}
      <ScrollReveal delay={100}>
      <section className="py-16 md:py-20 bg-redbean-cream">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: "1200px" }}>
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            <div className="flex-1 order-2 lg:order-1">
              <h2
                className="font-heading font-bold text-redbean-coffee mb-6"
                style={{ fontSize: "clamp(1.5rem, 3vw, 2.125rem)", lineHeight: 1.4 }}
              >
                {origin.sectionTitle}
              </h2>
              <p className="text-redbean-coffee/75" style={{ fontSize: "1.1875rem", lineHeight: 1.75 }}>
                {origin.body}
              </p>
            </div>
            <div className="flex-1 order-1 lg:order-2 w-full max-w-md lg:max-w-none">
              <div className="rounded-card overflow-hidden shadow-card" style={{ height: "380px" }}>
                <img
                  src="https://readdy.ai/api/search-image?query=artisan%20making%20taiyaki%20red%20bean%20cakes%20by%20hand%20on%20traditional%20griddle%20steam%20rising%20warm%20workshop%20lighting%20close-up%20hands%20shaping%20dough%20Taiwanese%20street%20food%20craftsman%20warm%20golden%20tones%20documentary%20style%20food%20photography&width=800&height=600&seq=bs-origin-1&orientation=landscape"
                  alt="職人現烤紅豆餅製作場景"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* Philosophy Section */}
      <ScrollReveal delay={150}>
      <section className="py-16 md:py-20 bg-redbean-lightcream">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: "1200px" }}>
          <h2
            className="font-heading font-bold text-redbean-coffee text-center mb-10 md:mb-12"
            style={{ fontSize: "clamp(1.5rem, 3vw, 2.125rem)", lineHeight: 1.4 }}
          >
            {philosophy.sectionTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {philosophy.items.map((item, idx) => (
              <ScrollReveal key={idx} delay={idx * 120}>
                <div className="bg-white rounded-card p-6 md:p-7 shadow-soft text-center">
                  <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-full bg-redbean-primary/10 text-redbean-primary">
                    <span className="w-7 h-7 flex items-center justify-center">
                      <i
                        className={
                          idx === 0
                            ? "ri-seedling-line text-2xl"
                            : idx === 1
                              ? "ri-fire-line text-2xl"
                              : "ri-heart-3-line text-2xl"
                        }
                      />
                    </span>
                  </div>
                  <h3
                    className="font-heading font-bold text-redbean-coffee mb-2"
                    style={{ fontSize: "1.25rem", lineHeight: 1.5 }}
                  >
                    {item.title}
                  </h3>
                  <p className="text-redbean-coffee/60 text-[1rem] leading-relaxed">{item.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* Spirit Section */}
      <ScrollReveal delay={200}>
      <section className="py-16 md:py-20 bg-redbean-cream">
        <div className="mx-auto px-4 md:px-6 lg:px-8 text-center" style={{ maxWidth: "800px" }}>
          <h2
            className="font-heading font-bold text-redbean-coffee mb-6"
            style={{ fontSize: "clamp(1.5rem, 3vw, 2.125rem)", lineHeight: 1.4 }}
          >
            {spirit.sectionTitle}
          </h2>
          <p className="text-redbean-coffee/70" style={{ fontSize: "1.1875rem", lineHeight: 1.75 }}>
            {spirit.body}
          </p>
        </div>
      </section>
      </ScrollReveal>

      {/* CTA */}
      <GlobalBottomCta />
    </main>
  );
}